import React, { useEffect, useRef } from "react";
import { Box, Card, IconButton, Typography } from "@mui/material";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import BookmarkIcon from "@mui/icons-material/BookmarkBorderOutlined";
import CloseIcon from "@mui/icons-material/CloseOutlined";

// A single bookmark tile. Mirrors the glass-card styling of the old dashboard
// shortcut grid, but the icon is always a generic bookmark (the tile is a
// user-curated link, not a fixed route). In manage mode the card becomes a
// drag handle (fixed-slot reorder via dnd-kit) and shows an X to delete; in
// normal mode it navigates on click.
export default function BookmarkCard({
  id,
  node,
  parentLabel,
  Icon,
  // Gated away for the selected school: greyed and non-navigable in normal mode,
  // but still fully manageable (drag/reorder/remove) in manage mode.
  disabled = false,
  manageMode,
  highlighted,
  focused,
  setItemRef,
  onFocus,
  onKeyDown,
  onActivate,
  onRemove,
  onLongPress,
  padding = 1,
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id, disabled: !manageMode });

  const dragStyle = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  // Press-and-hold (outside manage mode) enters the bookmarks "manage" state.
  // We start a timer on pointer down and, if it fires before the press ends,
  // call onLongPress and flag the gesture so the trailing click does not also
  // navigate.
  const longPressTimer = useRef(null);
  const longPressFired = useRef(false);

  const clearLongPress = () => {
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current);
      longPressTimer.current = null;
    }
  };

  const startLongPress = () => {
    if (manageMode || !onLongPress) return;
    longPressFired.current = false;
    clearLongPress();
    longPressTimer.current = setTimeout(() => {
      longPressFired.current = true;
      onLongPress();
    }, 500);
  };

  useEffect(() => clearLongPress, []);

  // A disabled tile only behaves disabled outside manage mode; in manage mode it
  // must stay draggable/removable. `dimmed` greys it, `interactive` gates the
  // navigate click + hover affordance.
  const disabledNow = disabled && !manageMode;
  const dimmed = isDragging || disabledNow;
  const interactive = !manageMode && !disabled;
  const disabledCursor = disabled ? "not-allowed" : "pointer";

  return (
    // dnd-kit's translate lives on this wrapper; the wiggle rotation lives on
    // the Card inside. Keeping them on separate elements lets the two transforms
    // compose instead of overwriting each other (which would break either the
    // reorder animation or the wiggle).
    <Box ref={setNodeRef} style={dragStyle} sx={{ position: "relative" }}>
      <Card
        ref={setItemRef}
        role="gridcell"
        tabIndex={manageMode || focused ? 0 : -1}
        aria-label={manageMode ? `Reorder ${node.label} bookmark` : node.label}
        aria-disabled={disabledNow || undefined}
        onClick={() => {
          if (manageMode) return;
          // Swallow the click that ends a long-press so it does not navigate.
          if (longPressFired.current) {
            longPressFired.current = false;
            return;
          }
          // Gated away at this school: not navigable (still manageable above).
          if (disabled) return;
          onActivate(node);
        }}
        onPointerDown={startLongPress}
        onPointerUp={clearLongPress}
        onPointerLeave={clearLongPress}
        onPointerCancel={clearLongPress}
        onFocus={onFocus}
        onKeyDown={onKeyDown}
        {...(manageMode ? attributes : {})}
        {...(manageMode ? listeners : {})}
        sx={(theme) => ({
          ...theme.custom.glassBox,
          borderRadius: 8, // double the glassBox default (4)
          aspectRatio: "1",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: 0.75,
          p: padding,
          cursor: manageMode ? "grab" : disabledCursor,
          opacity: dimmed ? 0.4 : 1,
          touchAction: manageMode ? "none" : "auto",
          userSelect: "none",
          transition:
            "opacity 120ms, filter 120ms, box-shadow 200ms, outline-color 200ms",
          // Lift the tile while it is actually being dragged.
          ...(isDragging && { cursor: "grabbing", boxShadow: 6, zIndex: 1 }),
          // Wiggle every tile while managing (except the one being dragged), the
          // way phone home screens jiggle in edit mode. A per-tile delay derived
          // from the id desyncs neighbours so they don't wobble in lockstep.
          "@keyframes bookmarkWiggle": {
            "0%": { transform: "rotate(-1.4deg)" },
            "50%": { transform: "rotate(1.4deg)" },
            "100%": { transform: "rotate(-1.4deg)" },
          },
          ...(manageMode &&
            !isDragging && {
              animation: "bookmarkWiggle 0.32s ease-in-out infinite",
              animationDelay: `${(id.length % 4) * 40}ms`,
            }),
          ...(highlighted && {
            outline: "2px solid",
            outlineColor: "primary.main",
            outlineOffset: 2,
            boxShadow: `0 0 0 4px ${theme.palette.primary.main}33`,
          }),
          // dnd-kit only flips isDragging once the pointer moves past its
          // activation threshold, so a plain press-and-hold would show nothing.
          // Mirror the dragging look on :active so picking a tile up in manage
          // mode reads instantly.
          "&:active": manageMode
            ? { opacity: 0.4, cursor: "grabbing" }
            : undefined,
          "&:hover": interactive ? { filter: "brightness(0.97)" } : {},
          "&:focus-visible": {
            outline: "2px solid",
            outlineColor: "primary.main",
            outlineOffset: "-2px",
          },
        })}
      >
        {Icon ? (
          <Icon sx={{ fontSize: 32, color: "#00000080" }} />
        ) : (
          <BookmarkIcon sx={{ fontSize: 32, color: "#00000080" }} />
        )}
        <Box sx={{ maxWidth: "100%" }}>
          {parentLabel && (
            <Typography
              sx={{
                fontSize: 10,
                lineHeight: 1.2,
                textAlign: "center",
                color: "text.secondary",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {parentLabel}
            </Typography>
          )}
          <Typography
            sx={{
              fontSize: 13,
              fontWeight: 500,
              lineHeight: 1.15,
              textAlign: "center",
              color: "text.primary",
              // Cap at two lines so a long label never overflows the square
              // and makes one tile taller than its neighbors.
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
            }}
          >
            {node.label}
          </Typography>
        </Box>
      </Card>

      {manageMode && (
        <IconButton
          size="small"
          aria-label={`Remove ${node.label} bookmark`}
          onClick={(e) => {
            e.stopPropagation();
            onRemove(id);
          }}
          sx={(theme) => ({
            position: "absolute",
            top: theme.spacing(0.5),
            right: theme.spacing(0.5),
            bgcolor: "background.paper",
            boxShadow: 1,
            p: 0.25,
            "&:hover": {
              bgcolor: "background.paper",
              filter: "brightness(0.95)",
            },
          })}
        >
          <CloseIcon sx={{ fontSize: 16 }} />
        </IconButton>
      )}
    </Box>
  );
}
